import React, { useState, useEffect } from 'react';
import { ApiService } from '../services/api';
import { VenueClaim, Venue } from '../types';
import { ConicGlowCard } from '../components/common/ConicGlowCard';
import {
  ShieldCheckIcon,
  SparklesIcon,
  BuildingIcon,
  GlobeIcon,
  ShareIcon,
  CheckIcon
} from '../components/common/Icons';

export const AdminConsolePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'enrichment' | 'claims' | 'unclaimed' | 'smtp'>('enrichment');

  // Stats
  const [stats, setStats] = useState<any>({
    venues: { total: 567, claimed: 8 },
    bookings: { total: 12, total_value: 8400000 },
    sessions: { total: 48 },
    owners: { total: 8 }
  });

  // Enrichment Sweep
  const [corridor, setCorridor] = useState('Karsaz');
  const [isSweeping, setIsSweeping] = useState(false);
  const [sweepResult, setSweepResult] = useState<string | null>(null);

  // Claims
  const [claims, setClaims] = useState<VenueClaim[]>([]);
  const [claimActionStatus, setClaimActionStatus] = useState<string | null>(null);

  // Unclaimed Venues & Partner Invite Generator
  const [venues, setVenues] = useState<Venue[]>([]);
  const [copiedInviteSlug, setCopiedInviteSlug] = useState<string | null>(null);

  // SMTP Test
  const [testEmail, setTestEmail] = useState('subhan.z.g@gmail.com');
  const [isTestingSmtp, setIsTestingSmtp] = useState(false);
  const [smtpResult, setSmtpResult] = useState<string | null>(null);

  const karachiCorridors = [
    'North Nazimabad',
    'Gulistan-e-Johar',
    'Korangi',
    'Malir',
    'Nazimabad',
    'Shahrah-e-Faisal',
    'DHA/Clifton',
    'Gulshan-e-Iqbal',
    'Federal B Area',
    'Bahria Town',
    'Tariq Road',
    'Karsaz'
  ];

  useEffect(() => {
    ApiService.adminGetStats()
      .then((res) => setStats(res))
      .catch((e) => console.error('Failed to load admin stats:', e));

    ApiService.adminGetClaims()
      .then((res) => setClaims(res.claims || []))
      .catch((e) => console.error('Failed to load claims:', e));

    ApiService.searchVenues({ limit: 20 })
      .then((res) => setVenues(res.venues || []))
      .catch((e) => console.error('Failed to load venues:', e));
  }, []);

  const handleRunSweep = async () => {
    try {
      setIsSweeping(true);
      setSweepResult(`Running Google Places (New) ingestion sweep for ${corridor}...`);
      const res = await ApiService.adminSweepEnrichment(corridor);
      setSweepResult(`✓ Sweep completed! Ingested ${res.ingestedCount} new Karachi banquets with AI social enrichment.`);
    } catch (err: any) {
      setSweepResult(`Error: ${err?.message || 'Sweep failed'}`);
    } finally {
      setIsSweeping(false);
    }
  };

  const handleReviewClaim = async (claimId: string, status: 'approved' | 'rejected') => {
    try {
      setClaimActionStatus(`Updating claim ${claimId}...`);
      await ApiService.adminReviewClaim(claimId, status);
      setClaims((prev) =>
        prev.map((c) => (c.id === claimId ? { ...c, status } : c))
      );
      setClaimActionStatus(`✓ Claim #${claimId} marked as ${status}!`);
      setTimeout(() => setClaimActionStatus(null), 3000);
    } catch (err: any) {
      setClaimActionStatus(`Error: ${err?.message || 'Action failed'}`);
    }
  };

  const handleCopyPartnerInvite = (venue: Venue) => {
    const inviteUrl = `${window.location.origin}/partner?claim=${encodeURIComponent(venue.slug)}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(inviteUrl).catch(() => {});
    }
    setCopiedInviteSlug(venue.slug);
    setTimeout(() => setCopiedInviteSlug(null), 2500);
  };

  const handleTestSmtp = async () => {
    try {
      setIsTestingSmtp(true);
      setSmtpResult('Testing Google Workspace SMTP Relay (smtp-relay.gmail.com:587) with EHLO intrepidtech.net...');
      const res = await ApiService.adminTestSmtp(testEmail);
      setSmtpResult(`✓ Success! MessageId: ${res.messageId}. Handshake confirmed with Google Workspace Relay.`);
    } catch (err: any) {
      setSmtpResult(`Error: ${err?.message || 'SMTP test failed'}`);
    } finally {
      setIsTestingSmtp(false);
    }
  };

  return (
    <div className="stage-deck-content">
      <div className="deck-header" style={{ marginBottom: '0.55rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
            <span className="pill-badge gemini">
              <ShieldCheckIcon size={12} />
              <span>Super Admin Console</span>
            </span>
            <span className="header-mobile-hide" style={{ fontSize: '0.78rem', color: 'hsl(var(--text-muted))' }}>
              mehfil@intrepidtech.net • GCP Project: spherical-plane-162307
            </span>
          </div>
          <h2 className="deck-title">Platform Operations &amp; Cloud Services</h2>
        </div>
      </div>

      {/* Metrics Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(9rem, 1fr))', gap: '0.55rem', marginBottom: '0.85rem' }}>
        <div className="glass-panel" style={{ padding: '0.72rem 0.85rem' }}>
          <span style={{ fontSize: '0.64rem', color: 'hsl(var(--text-muted))', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>
            Total Karachi Venues
          </span>
          <div className="num-tabular" style={{ fontSize: '1.38rem', fontWeight: 800, marginTop: '0.12rem' }}>
            {stats.venues?.total || 567}
          </div>
          <span style={{ fontSize: '0.66rem', color: 'hsl(var(--success))', fontWeight: 600 }}>
            {stats.venues?.claimed || 8} Claimed by Owners
          </span>
        </div>

        <div className="glass-panel" style={{ padding: '0.72rem 0.85rem' }}>
          <span style={{ fontSize: '0.64rem', color: 'hsl(var(--text-muted))', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>
            Active Booking Holds
          </span>
          <div className="num-tabular" style={{ fontSize: '1.38rem', fontWeight: 800, marginTop: '0.12rem', color: 'hsl(var(--accent-gold))' }}>
            {stats.bookings?.total || 12}
          </div>
          <span className="num-tabular" style={{ fontSize: '0.66rem', color: 'hsl(var(--text-muted))' }}>
            Rs. {Number(stats.bookings?.total_value || 8400000).toLocaleString()} GMV
          </span>
        </div>

        <div className="glass-panel" style={{ padding: '0.72rem 0.85rem' }}>
          <span style={{ fontSize: '0.64rem', color: 'hsl(var(--text-muted))', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>
            Customer Live Calls
          </span>
          <div className="num-tabular" style={{ fontSize: '1.38rem', fontWeight: 800, marginTop: '0.12rem', color: 'hsl(var(--glow-gemini-blue))' }}>
            {stats.sessions?.total || 48}
          </div>
          <span style={{ fontSize: '0.66rem', color: 'hsl(var(--text-muted))' }}>
            Gemini 3.8 Live 24kHz
          </span>
        </div>

        <div className="glass-panel" style={{ padding: '0.72rem 0.85rem' }}>
          <span style={{ fontSize: '0.64rem', color: 'hsl(var(--text-muted))', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>
            SMTP Relay 24h
          </span>
          <div className="num-tabular" style={{ fontSize: '1.38rem', fontWeight: 800, marginTop: '0.12rem', color: 'hsl(var(--accent-emerald))' }}>
            35
          </div>
          <span style={{ fontSize: '0.66rem', color: 'hsl(var(--success))', fontWeight: 600 }}>
            100% Workspace Delivery
          </span>
        </div>
      </div>

      {/* Navigation Tabs — Zero-Overflow Responsive Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(9rem, 1fr))', gap: '0.4rem', marginBottom: '0.9rem', borderBottom: '1px solid hsl(var(--surface-border) / 0.5)', paddingBottom: '0.6rem' }}>
        {[
          { id: 'enrichment' as const, label: 'Corridor Sweep' },
          { id: 'claims' as const, label: 'Owner Claims' },
          { id: 'unclaimed' as const, label: 'Partner Invite Links' },
          { id: 'smtp' as const, label: 'SMTP Relay Check' }
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
            style={{ justifyContent: 'center', padding: '0.36rem 0.5rem', fontSize: '0.74rem' }}
            onClick={() => setActiveTab(tab.id)}
          >
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* TAB 1: Places Enrichment Sweep */}
      {activeTab === 'enrichment' && (
        <div style={{ maxWidth: '42rem', margin: '0 auto' }}>
          <ConicGlowCard glowIntensity={0.65}>
            <div style={{ padding: '1.25rem' }}>
              <h3 style={{ fontSize: '1.08rem', fontWeight: 800, marginBottom: '0.28rem', display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                <GlobeIcon size={18} />
                <span>Google Places (New) Corridor Batch Ingestion</span>
              </h3>
              <p style={{ fontSize: '0.76rem', color: 'hsl(var(--text-muted))', marginBottom: '0.95rem' }}>
                Triggers server-side Places API (New) sweep, sharp WebP/AVIF transcoding, and Gemini social profile enrichment
              </p>

              <div style={{ marginBottom: '0.95rem' }}>
                <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.3rem' }}>
                  Target Karachi Wedding Corridor
                </label>
                <select
                  value={corridor}
                  onChange={(e) => setCorridor(e.target.value)}
                  className="form-select"
                >
                  {karachiCorridors.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {sweepResult && (
                <div
                  style={{
                    padding: '0.65rem 0.85rem',
                    borderRadius: '0.5rem',
                    backgroundColor: sweepResult.startsWith('Error') ? 'hsl(var(--danger) / 0.15)' : 'hsl(var(--glow-gemini-blue) / 0.15)',
                    border: '1px solid currentColor',
                    color: sweepResult.startsWith('Error') ? 'hsl(var(--danger))' : 'hsl(var(--text-primary))',
                    fontSize: '0.76rem',
                    marginBottom: '0.95rem'
                  }}
                >
                  {sweepResult}
                </div>
              )}

              <button
                type="button"
                className="btn-primary"
                style={{ width: '100%', height: '2.65rem', fontSize: '0.86rem' }}
                disabled={isSweeping}
                onClick={handleRunSweep}
              >
                <SparklesIcon size={15} />
                <span>{isSweeping ? 'Ingesting & Transcoding Images...' : `Run Sweep for ${corridor}`}</span>
              </button>
            </div>
          </ConicGlowCard>
        </div>
      )}

      {/* TAB 2: Owner Claim Approvals */}
      {activeTab === 'claims' && (
        <div style={{ maxWidth: '52rem', margin: '0 auto' }}>
          <div className="glass-panel" style={{ padding: '2rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.375rem' }}>
              Pending Hall Owner Venue Claims
            </h3>
            <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--text-muted))', marginBottom: '1.5rem' }}>
              Review business phone verifications and proof notes to grant verified banquet ownership
            </p>

            {claimActionStatus && (
              <div
                style={{
                  padding: '0.625rem 0.875rem',
                  borderRadius: '0.5rem',
                  backgroundColor: 'hsl(var(--success) / 0.15)',
                  border: '1px solid hsl(var(--success) / 0.4)',
                  color: 'hsl(var(--success))',
                  fontSize: '0.8125rem',
                  marginBottom: '1.25rem'
                }}
              >
                {claimActionStatus}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {claims.length > 0 ? (
                claims.map((claim) => (
                  <div
                    key={claim.id}
                    className="glass-panel"
                    style={{
                      padding: '1.25rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h4 style={{ fontSize: '1rem', fontWeight: 700 }}>
                          {claim.venueName || `Venue #${claim.venueId}`}
                        </h4>
                        <span style={{ fontSize: '0.75rem', color: 'hsl(var(--text-muted))' }}>
                          Claimed by: {claim.userName || 'Owner'} ({claim.userEmail}) • Tel: {claim.contactPhone}
                        </span>
                      </div>
                      <span
                        className="pill-badge"
                        style={{
                          backgroundColor:
                            claim.status === 'approved'
                              ? 'hsl(var(--success) / 0.2)'
                              : claim.status === 'rejected'
                              ? 'hsl(var(--danger) / 0.2)'
                              : 'hsl(var(--warning) / 0.2)',
                          color:
                            claim.status === 'approved'
                              ? 'hsl(var(--success))'
                              : claim.status === 'rejected'
                              ? 'hsl(var(--danger))'
                              : 'hsl(var(--warning))'
                        }}
                      >
                        {claim.status.toUpperCase()}
                      </span>
                    </div>

                    {claim.proofNotes && (
                      <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--text-secondary))', fontStyle: 'italic' }}>
                        Proof Notes: "{claim.proofNotes}"
                      </p>
                    )}

                    {claim.status === 'pending' && (
                      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                        <button
                          type="button"
                          className="btn-primary"
                          style={{ padding: '0.4rem 1rem', fontSize: '0.8125rem', backgroundColor: 'hsl(var(--success))' }}
                          onClick={() => handleReviewClaim(claim.id, 'approved')}
                        >
                          <span>Approve Claim</span>
                        </button>
                        <button
                          type="button"
                          className="btn-secondary"
                          style={{ padding: '0.4rem 1rem', fontSize: '0.8125rem' }}
                          onClick={() => handleReviewClaim(claim.id, 'rejected')}
                        >
                          <span>Reject</span>
                        </button>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '2.5rem 0', color: 'hsl(var(--text-muted))', fontSize: '0.875rem' }}>
                  No pending venue claims in queue. All verified claims are active.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: Unclaimed Venues & Partner Onboarding Link Generator */}
      {activeTab === 'unclaimed' && (
        <div style={{ maxWidth: '54rem', margin: '0 auto' }}>
          <div className="glass-panel" style={{ padding: '2rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.35rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <BuildingIcon size={20} />
              <span>Unclaimed Karachi Venues & Partner Onboarding Links</span>
            </h3>
            <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--text-muted))', marginBottom: '1.5rem' }}>
              Generate 1-click onboarding links or WhatsApp invites for banquet managers when a customer places a hold on an unclaimed hall
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              {venues.map((v) => (
                <div
                  key={v.id}
                  className="glass-panel"
                  style={{
                    padding: '1rem 1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '1rem'
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontWeight: 800, fontSize: '0.95rem' }}>{v.name}</span>
                      <span className={`pill-badge ${v.isClaimed ? 'verified' : 'pricing'}`} style={{ fontSize: '0.65rem' }}>
                        {v.isClaimed ? 'Claimed Partner' : 'Unclaimed (Market Est.)'}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'hsl(var(--text-muted))', marginTop: '0.15rem' }}>
                      {v.area} • Capacity: {v.minCapacity}–{v.maxCapacity} pax
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ padding: '0.4rem 0.85rem', fontSize: '0.78rem' }}
                      onClick={() => handleCopyPartnerInvite(v)}
                    >
                      {copiedInviteSlug === v.slug ? (
                        <>
                          <CheckIcon size={14} />
                          <span>Invite Link Copied!</span>
                        </>
                      ) : (
                        <>
                          <ShareIcon size={14} />
                          <span>Copy Partner Onboarding Link</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: SMTP Relay Health Check */}
      {activeTab === 'smtp' && (
        <div style={{ maxWidth: '40rem', margin: '0 auto' }}>
          <ConicGlowCard glowIntensity={0.6}>
            <div style={{ padding: '2rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.375rem' }}>
                Google Workspace SMTP Relay Diagnostic
              </h3>
              <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--text-muted))', marginBottom: '1.5rem' }}>
                Verifies end-to-end delivery through <code>smtp-relay.gmail.com:587</code> with EHLO <code>intrepidtech.net</code>
              </p>

              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.375rem' }}>
                  Diagnostic Recipient Email
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="form-input"
                />
              </div>

              {smtpResult && (
                <div
                  style={{
                    padding: '0.75rem 1rem',
                    borderRadius: '0.5rem',
                    backgroundColor: smtpResult.startsWith('Error') ? 'hsl(var(--danger) / 0.15)' : 'hsl(var(--success) / 0.15)',
                    border: '1px solid currentColor',
                    color: smtpResult.startsWith('Error') ? 'hsl(var(--danger))' : 'hsl(var(--text-primary))',
                    fontSize: '0.8125rem',
                    marginBottom: '1.25rem',
                    wordBreak: 'break-all'
                  }}
                >
                  {smtpResult}
                </div>
              )}

              <button
                type="button"
                className="btn-primary"
                style={{ width: '100%', height: '3rem', fontSize: '0.9375rem' }}
                disabled={isTestingSmtp}
                onClick={handleTestSmtp}
              >
                <span>{isTestingSmtp ? 'Transmitting Handshake...' : 'Trigger SMTP Relay Test'}</span>
              </button>
            </div>
          </ConicGlowCard>
        </div>
      )}
    </div>
  );
};
